// Firebase Repository for Deal operations
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { Deal, dealFromMap } from '../types/deal';
import { OnboardingSessionData } from '../types/onboardingSession';
import { getCachedDeal, setCachedDeal } from '../utils/dealCache';

/**
 * Fetch deal from Firestore by deal ID, with a stale-while-revalidate cache.
 * If a cached deal exists it's returned immediately (instant on repeat visits)
 * and refreshed from Firestore in the background for next time.
 * Matches Flutter's getDeal() implementation.
 */
export const getDeal = async (dealId: string): Promise<Deal | null> => {
  const cached = getCachedDeal(dealId);
  if (cached) {
    console.log('[FirebaseRepo] ⚡ Deal served from cache:', dealId);
    // Refresh in the background so the next visit has fresh data
    fetchDealFromFirestore(dealId).then((fresh) => { if (fresh) setCachedDeal(fresh); }).catch(() => {});
    return cached;
  }
  const fresh = await fetchDealFromFirestore(dealId);
  if (fresh) setCachedDeal(fresh);
  return fresh;
};

const fetchDealFromFirestore = async (dealId: string): Promise<Deal | null> => {
  try {
    console.log('[FirebaseRepo] 🔍 Fetching deal from Firestore...');
    console.log('[FirebaseRepo] Path: /deals/' + dealId);

    const dealDocRef = doc(db, 'deals', dealId);
    const dealDoc = await getDoc(dealDocRef);

    if (dealDoc.exists()) {
      const data = dealDoc.data();
      console.log('[FirebaseRepo] ✅ Deal document found!');
      console.log('[FirebaseRepo] Raw data from Firestore:', JSON.stringify(data, null, 2));
      
      // Map Firestore data to Deal model
      const deal = dealFromMap({
        id: dealDoc.id,
        ...data,
      });
      
      console.log('[FirebaseRepo] ✅ Deal mapped successfully:', {
        id: deal.id,
        name: deal.name,
        message: deal.message
      });
      
      return deal;
    }
    
    console.error('[FirebaseRepo] ❌ Deal document does not exist!');
    console.error('[FirebaseRepo] Checked path: /deals/' + dealId);
    console.error('[FirebaseRepo] Action needed: Create this document in Firebase Console');
    return null;
  } catch (error: any) {
    console.error('[FirebaseRepo] ❌ Error fetching deal from Firestore!');
    console.error('[FirebaseRepo] Error type:', error.constructor.name);
    console.error('[FirebaseRepo] Error message:', error.message);
    console.error('[FirebaseRepo] Full error:', error);
    return null;
  }
};

/**
 * Save deal to user's profile in Firestore
 * Saves to users/{userId}/settings.active_deal
 * Creates the document if it doesn't exist
 */
export const saveDealToUserProfile = async (
  userId: string,
  deal: Deal
): Promise<boolean> => {
  try {
    console.log('[FirebaseRepo] Saving deal to user profile:', userId, deal.id);
    
    const { doc: userDoc, setDoc } = await import('firebase/firestore');
    const userDocRef = userDoc(db, 'users', userId);
    
    // Use setDoc with merge to create document if it doesn't exist
    await setDoc(userDocRef, {
      id: userId,
      settings: {
        active_deal: {
          id: deal.id,
          name: deal.name,
          message: deal.message,
          logo: deal.logo,
          description: deal.description,
          backgroundColor: deal.backgroundColor,
          textColor: deal.textColor,
          authImage: deal.authImage ?? null,
          welcomeImage: deal.welcomeImage ?? null,
          imageLayout: deal.imageLayout ?? null,
          logoLayout: deal.logoLayout ?? null,
          callout: deal.callout ?? null,
          translations: deal.translations ?? null,
          defaultLanguage: deal.defaultLanguage ?? null,
          headline: deal.headline ?? null,
          ctaText: deal.ctaText ?? null,
          isRedemption: deal.isRedemption ?? true,
          appliedAt: new Date().toISOString(),
        }
      }
    }, { merge: true });
    
    console.log('[FirebaseRepo] Deal saved to user profile');
    return true;
  } catch (error) {
    console.error('[FirebaseRepo] Error saving deal to user profile:', error);
    return false;
  }
};

/**
 * Clear active deal from user's profile
 */
export const clearActiveDealFromUserProfile = async (
  userId: string
): Promise<boolean> => {
  try {
    const { doc: userDoc, setDoc } = await import('firebase/firestore');
    const userDocRef = userDoc(db, 'users', userId);
    
    // Use setDoc with merge to avoid creating document if it doesn't exist
    await setDoc(userDocRef, {
      settings: {
        active_deal: null
      }
    }, { merge: true });
    
    console.log('[FirebaseRepo] Active deal cleared from user profile');
    return true;
  } catch (error) {
    console.error('[FirebaseRepo] Error clearing active deal from user profile:', error);
    return false;
  }
};

/**
 * Create a user document in Firestore
 * Called when a new user signs up
 */
export const createUserDocument = async (
  userId: string,
  email: string | null,
  emailSubscriptionsEnabled?: boolean,
  onboardingSession?: OnboardingSessionData | null
): Promise<boolean> => {
  try {
    console.log('[FirebaseRepo] Creating user document:', userId);

    const { doc: userDoc, setDoc } = await import('firebase/firestore');
    const userDocRef = userDoc(db, 'users', userId);

    // Build the full document in one write
    const userData: Record<string, any> = {
      id: userId,
      email: email || '',
      created_at: new Date().toISOString(),
    };

    // Settings
    const settings: Record<string, any> = {};
    if (emailSubscriptionsEnabled !== undefined) {
      settings.email_subscriptions_enabled = emailSubscriptionsEnabled;
    }

    // Attach pending Everflow transaction ID from Branch deep link
    const pendingEverflowTid = localStorage.getItem('pending_everflow_tid');
    if (pendingEverflowTid) {
      settings.everflow_transaction_id = pendingEverflowTid;
      localStorage.removeItem('pending_everflow_tid');
      console.log('[FirebaseRepo] Attached Everflow transaction ID:', pendingEverflowTid);
    }

    // Onboarding data — only save if user actually completed onboarding with valid data
    const hasValidOnboarding = onboardingSession
      && onboardingSession.first_name
      && onboardingSession.onboarding_data
      && Object.keys(onboardingSession.onboarding_data).length > 0;

    if (hasValidOnboarding) {
      userData.first_name = onboardingSession.first_name;
      userData.last_name = onboardingSession.last_name || null;
      userData.info = {
        first_name: onboardingSession.first_name,
        last_name: onboardingSession.last_name || null,
        gender: onboardingSession.gender,
        birth_date: onboardingSession.birth_date,
      };
      userData.onboarding_data = onboardingSession.onboarding_data;
      settings.is_onboarding_completed = true;
      settings.is_onboarding_v2_completed = true;
    }

    if (Object.keys(settings).length > 0) {
      userData.settings = settings;
    }

    await setDoc(userDocRef, userData, { merge: true });

    console.log('[FirebaseRepo] ✅ User document created successfully');
    return true;
  } catch (error) {
    console.error('[FirebaseRepo] ❌ Error creating user document:', error);
    return false;
  }
};

/**
 * Update email subscription preference for a user
 */
export const updateEmailSubscriptionPreference = async (
  userId: string,
  enabled: boolean
): Promise<boolean> => {
  try {
    console.log('[FirebaseRepo] Updating email subscription preference:', userId, enabled);

    const { doc: userDoc, setDoc } = await import('firebase/firestore');
    const userDocRef = userDoc(db, 'users', userId);

    await setDoc(userDocRef, {
      settings: {
        email_subscriptions_enabled: enabled,
      }
    }, { merge: true });

    console.log('[FirebaseRepo] ✅ Email subscription preference updated');
    return true;
  } catch (error) {
    console.error('[FirebaseRepo] ❌ Error updating email subscription preference:', error);
    return false;
  }
};

